import { Location } from '@/types';

export interface PaginatedResponse<T> {
  status: boolean;
  message: string;
  data: T[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

export interface LazyLoadOptions {
  page?: number;
  perPage?: number;
  useCache?: boolean;
}

// In-memory cache for location data
interface LocationCache {
  data: Location[] | null;
  timestamp: number;
  TTL: number; // 5 minutes
}

class LocationService {
  private static API_URL = '/api/location';
  private static cache: LocationCache = {
    data: null,
    timestamp: 0,
    TTL: 5 * 60 * 1000, // 5 minutes
  };

  // Legacy method for backward compatibility
  static async fetchLocations(perPage: number = 1000): Promise<Location[]> {
    return this.fetchLocationsLazy({ perPage, useCache: true });
  }

  // New lazy loading method with smaller pagination
  static async fetchLocationsLazy(options: LazyLoadOptions = {}): Promise<Location[]> {
    const { page = 1, perPage = 50, useCache = true } = options;

    // Check cache first if enabled
    if (useCache && this.isCacheValid()) {
      const cachedData = this.cache.data!;
      // Return paginated subset from cache
      const startIndex = (page - 1) * perPage;
      const endIndex = startIndex + perPage;
      return cachedData.slice(startIndex, endIndex);
    }

    const url = `${this.API_URL}?page=${page}&perPage=${perPage}`;

    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Error fetching locations: ${res.status} ${res.statusText}`);
    }

    const json: PaginatedResponse<Location> = await res.json();
    const locations = Array.isArray(json.data) ? json.data : [];

    // Update cache if this is the first page and cache is enabled
    if (useCache && page === 1) {
      this.updateCache(locations);
    }

    return locations;
  }

  // Observer-based fetch for intersection observer
  static async fetchLocationsOnDemand(options: LazyLoadOptions = {}): Promise<Location[]> {
    const { perPage = 20, useCache = true } = options;
    
    // Use smaller pagination for on-demand loading
    return this.fetchLocationsLazy({ page: 1, perPage, useCache });
  }

  // Cache management methods
  private static isCacheValid(): boolean {
    const now = Date.now();
    return this.cache.data !== null && (now - this.cache.timestamp) < this.cache.TTL;
  }

  private static updateCache(locations: Location[]): void {
    this.cache.data = locations;
    this.cache.timestamp = Date.now();
  }

  static clearCache(): void {
    this.cache.data = null;
    this.cache.timestamp = 0;
  }

  // Get cached data without API call
  static getCachedLocations(): Location[] | null {
    return this.isCacheValid() ? this.cache.data : null;
  }
}

export default LocationService;

